import React, { useEffect, useId, useRef, useState } from 'react';

/**
 * Progressive disclosure primitives for the playing platform
 * (Lichess / chess.com pattern: primary surface quiet; secondary behind request).
 */

/** Icon button used in compact game toolbars. */
export function PlayingIconBtn({
  label,
  onClick,
  disabled,
  danger,
  active,
  children,
  className = '',
}) {
  return (
    <button
      type="button"
      className={[
        'playing-icon-btn',
        danger ? 'playing-icon-btn--danger' : '',
        active ? 'playing-icon-btn--active' : '',
        className,
      ].filter(Boolean).join(' ')}
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

/** Click-outside menu (tools / ⋯). */
export function PlayingMenu({
  label,
  open,
  onOpenChange,
  align = 'end',
  children,
  trigger,
}) {
  const rootRef = useRef(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        onOpenChange(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') onOpenChange(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onOpenChange]);

  return (
    <div className="playing-menu" ref={rootRef}>
      <button
        type="button"
        className={`playing-icon-btn${open ? ' playing-icon-btn--active' : ''}`}
        aria-label={label}
        title={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => onOpenChange(!open)}
      >
        {trigger || (
          <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden>
            <circle cx="5" cy="12" r="1.75" />
            <circle cx="12" cy="12" r="1.75" />
            <circle cx="19" cy="12" r="1.75" />
          </svg>
        )}
      </button>
      {open && (
        <div
          id={menuId}
          role="menu"
          className={`playing-menu__panel playing-menu__panel--${align}`}
        >
          {children}
        </div>
      )}
    </div>
  );
}

export function PlayingMenuItem({
  onClick,
  disabled,
  danger,
  children,
  checked,
}) {
  return (
    <button
      type="button"
      role="menuitem"
      className={`playing-menu__item${danger ? ' playing-menu__item--danger' : ''}`}
      disabled={disabled}
      onClick={onClick}
    >
      <span className="playing-menu__item-label">{children}</span>
      {checked != null && (
        <span className="playing-menu__item-check" aria-hidden>
          {checked ? '✓' : ''}
        </span>
      )}
    </button>
  );
}

export function PlayingMenuSeparator() {
  return <div className="playing-menu__sep" role="separator" />;
}

/** Panel that stays closed until the user expands it (settings, chat, …). */
export function PlayingDisclosure({
  title,
  hint,
  open,
  onOpenChange,
  badge,
  children,
  className = '',
  defaultOpen = false,
}) {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isControlled = typeof open === 'boolean';
  const expanded = isControlled ? open : internalOpen;
  const setExpanded = (next) => {
    if (!isControlled) setInternalOpen(next);
    onOpenChange?.(next);
  };
  const panelId = useId();

  return (
    <section className={`playing-disclosure ${expanded ? 'playing-disclosure--open' : ''} ${className}`.trim()}>
      <button
        type="button"
        className="playing-disclosure__toggle"
        aria-expanded={expanded}
        aria-controls={panelId}
        onClick={() => setExpanded(!expanded)}
      >
        <span className="playing-disclosure__main">
          <span className="playing-disclosure__title">
            {title}
            {badge != null && badge !== '' && badge !== 0 && (
              <span className="playing-hub-count">{badge}</span>
            )}
          </span>
          {hint && !expanded && <span className="playing-disclosure__hint">{hint}</span>}
        </span>
        <span className="playing-disclosure__chevron" aria-hidden>
          {expanded ? '▾' : '▸'}
        </span>
      </button>
      {expanded && (
        <div id={panelId} className="playing-disclosure__body">
          {children}
        </div>
      )}
    </section>
  );
}
